import { Serialiser } from "hooks/serialisers/types";
import { Setter } from "hooks/state/syncing";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";

const isUpdater = <T>(
  update: T | ((current: T) => T)
): update is (current: T) => T => typeof update === "function";

export const useQueryParamState = <
  T extends object | string | number | null,
  O,
>(
  key: string,
  serialiser: Serialiser<T, O>,
  defaultValue: T
): [T, Setter<T>] => {
  const [searchParams] = useSearchParams({
    [key]: serialiser.format(defaultValue),
  });
  const navigate = useNavigate();

  const updateQueryParam = useCallback(
    (newValue: T) => {
      const currentParams = Object.fromEntries(
        new URLSearchParams(window.location.search).entries()
      );
      void navigate(
        {
          pathname: window.location.pathname,
          search: new URLSearchParams({
            ...currentParams,
            [key]: serialiser.format(newValue),
          }).toString(),
        },
        { replace: true }
      );
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- serialiser object identity is unstable; depend on serialiser.format (the specific method used) to avoid infinite re-renders
    [key, serialiser.format, navigate]
  );

  const param = searchParams.get(key) ?? "";
  const value = useMemo(() => {
    const result = serialiser.parse(param);
    return result.valid ? result.value : defaultValue;
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- serialiser object identity is unstable; depend on serialiser.parse and key
  }, [param, key, serialiser]);

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const setter = useCallback<Setter<T>>(
    (update) => {
      updateQueryParam(isUpdater(update) ? update(valueRef.current) : update);
    },
    [updateQueryParam]
  );

  return [value, setter];
};

/** Synchronises externally owned state with a query parameter. */
export const useSyncedQueryParam = <T extends object | string | null, O>(
  value: T,
  setValue: (newValue: T) => unknown,
  key: string,
  serialiser: Serialiser<T, O>
) => {
  const [queryParamValue, setQueryParam] = useQueryParamState<T, O>(
    key,
    serialiser,
    value
  );

  useEffect(() => {
    if (queryParamValue !== value) setQueryParam(value);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- only push state to URL when value changes; including queryParamValue creates bidirectional sync loop
  }, [value]);

  useEffect(() => {
    setValue(queryParamValue);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: initialize from query param, then only push state to URL
  }, []);
};
